import { Set as ImmutableSet } from 'immutable';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as investmentsQuotesDetailedSelectors from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedSelectors';
import { keyFromSecurity } from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedUtils';
import { createAppSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { investmentQuotesV2Selectors, investmentQuotesV2Utils } from '../investment-quotes-v2';
import { SECURITIES_REDUCER_KEY, type SecurityProps } from './securitiesTypes';
import { getPrice, getUnknownSecurity } from './securitiesUtils';

type State = Pick<RootState, typeof SECURITIES_REDUCER_KEY>;

export const getStore = (state: State) => state?.[SECURITIES_REDUCER_KEY];

export const getResourcesById = (state: State) => getStore(state)?.resourcesById;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;

export const getResourceById = (state: State, id: string) => getResourcesById(state)?.get(id);

const getQuoteBySecurity = (security: SecurityProps | undefined, quotes: any) => {
  const symbol = security?.symbol || null;
  const quote = investmentQuotesV2Utils.getQuoteBySymbol(quotes, symbol);

  return quote || security || getUnknownSecurity();
};

export const getSymbols = createAppSelector(
  (state?: State, securities?: ReturnType<typeof getResourcesById>) => securities || (state && getResourcesById(state)),
  (securities) =>
    ImmutableSet().withMutations(
      (symbols) =>
        securities &&
        securities.reduce((set, security) => (security?.symbol ? set.add(security.symbol?.trim()) : set), symbols),
    ),
);

export const getQuotePricesBySecurityId = createAppSelector(
  getResourcesById,
  investmentQuotesV2Selectors.getResourcesById,
  (state: Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0]) =>
    featureFlagsSelectors.getFeatureFlag(state, 'investmentsDetailedQuotesRelease'),
  investmentsQuotesDetailedSelectors.getDetailedQuotesById,
  (securities, quotes, investmentsDetailedQuotesRelease, quotesDetailed) =>
    securities.map((mySecurity) => {
      if (investmentsDetailedQuotesRelease) {
        const key = keyFromSecurity(mySecurity);
        const quote = quotesDetailed[key ?? ''];
        return quote?.lastPrice ?? mySecurity.currentUnitPrice;
      } else {
        const quote = getQuoteBySecurity(mySecurity, quotes);
        return getPrice(quote);
      }
    }),
);

// searches the list of manual investment holdings created by the user that matches the
// params of symbol and name because QCS randomly populated of the securities list
export const getSecurityBySymbolOrName = createAppSelector(
  getResourcesById,
  (_state: unknown, symbol: string | undefined) => symbol,
  (_state: unknown, _symbol: unknown, holdingName?: string) => holdingName,
  (holdings, symbol, holdingName) =>
    holdings.find((p) => p.name === holdingName) || holdings.find((p) => p.symbol === symbol),
);

export const getQuoteByLocalSecurityId = createAppSelector(
  (state: State, localSecurityId: string) => getResourceById(state, localSecurityId),
  investmentQuotesV2Selectors.getResourcesById,
  (security, quotes) => getQuoteBySecurity(security, quotes),
);
